import { Grid, Paper } from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Alert from 'components/Alert';
import Spin from 'components/Spin';
import { agent } from 'helpers';
import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { toast } from 'react-toastify';
import { API_MYEDUZZ } from 'settings';
import Content from 'v2/components/Shared/Content';
import { useHint } from 'v2/components/Shared/Hint';
import ModalFiscal from 'v2/components/Shared/ModalFiscal';
import { EHintPath } from 'v2/interfaces/hint';

import type IProfile from 'v2/interfaces/profile';

import Address from './address';
import Contact from './contact';
import General from './general';
import Password from './password';
import Terms from './Terms/terms';
import Profile from './validations';

import type { RouteComponentProps } from 'react-router-dom';

const useStyle = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(1),
  },
  title: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-between',
    color: '#192542',
    '& h1': {
      fontSize: '2rem',
      lineHeight: 1,
      fontWeight: 400,
      marginBottom: '0.5rem',
    },
    '& p': {
      fontSize: '1rem',
      lineHeight: '1.3125rem',
      color: '#818b9d',
    },
  },
  alert: {
    width: '100%',
    padding: theme.spacing(2),
    textAlign: 'center',
    backgroundColor: '#192442',
    color: '#ffffff',
    maxWidth: 442,
    margin: '0 auto',
  },
}));

export interface IPasswords {
  current_password: string;
  new_password: string;
  repeated_new_password: string;
}

interface IProps extends RouteComponentProps<{}> {}

function Edit(props: IProps) {
  const classes = useStyle(props);
  const [profile, setProfile] = useState<IProfile>();
  const [isSupport, setSupport] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalFiscal, setModalFiscal] = useState<boolean>(false);
  const [whichIsLoading, setWhichIsLoading] = useState<string>();

  useHint(EHintPath.PROFILE);

  const saveProfile = async (newProfile: Partial<IProfile>) => {
    const data = { ...newProfile, fiscal_responsibility: profile.fiscal_responsibility };

    if (typeof data.profile_photo !== 'number') {
      delete data.profile_photo;
    }

    //TODO fazer que API receba URL
    try {
      const result = await agent.put(`${API_MYEDUZZ}/profile`, data);
      setProfile(result.data.data.profile);
      return true;
    } catch (error) {
      return false;
    }
  };

  const onGenericSubmit = async (newProfile: IProfile, formLoading: string) => {
    setWhichIsLoading(formLoading);
    const result = await saveProfile(newProfile);
    setWhichIsLoading(null);
    if (result) return toast.success('Dados do perfil salvo com sucesso!');
    return toast.error('Oops, ocorreu um erro ao tentar salvar seu perfil');
  };

  const updateFieldsRequiredForAPI = (profile: IProfile) => {
    setProfile(profile);
    setModalFiscal(false);
  };

  const onCancelModalFiscal = async () => {
    setModalFiscal(false);
    const { history } = props;

    const confirmation = await Alert.show({
      title: 'Atenção',
      message:
        // eslint-disable-next-line max-len
        'É necessário preencher as informações e aceitar os termos para conseguir editar seu perfil, deseja realmente cancelar? Caso você deseje realmente cancelar, você será redirecionado.',
      confirmation: true,
      buttonOk: 'Preencher informações',
      buttonCancel: 'Cancelar',
      small: true,
    });

    if (!confirmation) {
      history.push('/');
      return;
    }

    setModalFiscal(true);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        const result = await agent.get(`${API_MYEDUZZ}/profile`);
        setProfile(result.data.data);
        setIsLoading(false);

        if (!result?.data?.data?.fiscal_responsibility) {
          setModalFiscal(true);
        }
      } catch (error) {
        toast.error(error.message);
        setIsLoading(false);
      }
    };

    const fetchDataSupport = async () => {
      try {
        const result = await agent.get(`${API_MYEDUZZ}/auth/details`);
        setSupport(result.data.data.customer.is_support);
      } catch (error) {
        toast.error(error.message);
      }
    };

    if (!profile && !isLoading) {
      fetchData();
      fetchDataSupport();
    }
  }, [profile, isLoading]);

  const loading = !profile || isLoading;

  if (loading) {
    return <Spin />;
  }

  return (
    <>
      <div className={classes.title}>
        <div>
          <h1>Dados Cadastrais</h1>
          <p>Edite aqui seus dados cadastrais.</p>
        </div>
      </div>

      <Grid className={classes.root} container spacing={2}>
        <Grid item xs={12}>
          {isSupport && (
            <Paper className={classes.alert}>
              <h4>
                Você está logado como <span>SUPORTE</span>.
              </h4>
            </Paper>
          )}
        </Grid>

        <ModalFiscal
          opened={modalFiscal}
          onClose={() => onCancelModalFiscal()}
          onComplete={updateFieldsRequiredForAPI}
          returnDataOnComplete
          subtitle='Para continuar com sua edição, preencha os campos e aceite os termos abaixo:'
          profile={profile}
        />

        <Content>
          {[
            {
              id: 'basic',
              title: 'Dados básicos',
              anchor: 'Dados Básicos',
              validations: Profile.generalSchema,
              submit: (newProfile: IProfile) => onGenericSubmit(newProfile, 'basic'),
              defaultValues: profile,
              isLoading: whichIsLoading === 'basic',
              content: (cProps: any) => <General {...cProps} />,
            },
            {
              id: 'address',
              title: 'Endereço',
              anchor: 'Endereço',
              validations: Profile.addressSchema,
              submit: (newProfile: IProfile) => onGenericSubmit(newProfile, 'address'),
              defaultValues: profile,
              isLoading: whichIsLoading === 'address',
              content: (cProps: any) => <Address {...cProps} />,
            },
            {
              id: 'contact',
              title: 'Informações para contato',
              anchor: 'Informações para contato',
              validations: Profile.contactSchema,
              submit: (newProfile: IProfile) => onGenericSubmit(newProfile, 'contact'),
              defaultValues: profile,
              isLoading: whichIsLoading === 'contact',
              content: (cProps: any) => <Contact {...cProps} />,
            },
            {
              id: 'password',
              title: 'Alteração de senha',
              anchor: 'Alteração de senha',
              content: () => <Password />,
            },
            {
              id: 'terms',
              title: '',
              anchor: 'Termos e políticas',
              content: () => <Terms />,
            },
          ]}
        </Content>
      </Grid>
    </>
  );
}

export default withRouter(Edit);
